import React, { useRef, useState } from 'react';
import { Star, Paperclip, X, Loader2, FileText } from 'lucide-react';
import { FIELD_TYPES } from './fieldTypes';

function formatBytes(bytes) {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

// Colors reference CSS custom properties (set by FormRenderer from the
// form's theme, falling back to the brand default) rather than hardcoded
// hex, so a per-form theme color repaints every rendered field/button
// without each field needing its own theme-awareness.
const inputClass = "w-full px-3 py-2 border border-[#D8DDE6] rounded-[var(--form-radius,4px)] text-[14px] text-[#181818] bg-white focus:outline-none focus:border-[var(--form-primary,#0176D3)] focus:ring-4 focus:ring-[var(--form-primary,#0176D3)]/10 transition-all";
const errorInputClass = "border-[#EA001E] focus:border-[#EA001E] focus:ring-[#EA001E]/10";

/**
 * Renders ONE component from a form schema. This is the piece that grows
 * as new field types are registered in fieldTypes.js — it never needs to
 * know about pages, navigation, or the submission lifecycle.
 */
export default function FieldRenderer({ component, value, error, onChange, onUploadFile }) {
  const meta = FIELD_TYPES[component.type];
  if (!meta) {
    return (
      <div className="text-[12px] text-[#706E6B] italic">
        Unknown field type "{component.type}"
      </div>
    );
  }

  if (component.type === 'heading') {
    const Tag = component.level === 2 ? 'h3' : 'h2';
    return <Tag className="text-[18px] font-bold text-[#181818]">{component.label}</Tag>;
  }
  if (component.type === 'paragraph') {
    return <p className="text-[13px] text-[#706E6B] leading-relaxed">{component.text || component.label}</p>;
  }
  if (component.type === 'hidden') {
    return null;
  }

  const label = (
    <label className="text-[13px] font-medium text-[#181818] block mb-1.5">
      {component.label}
      {component.required && <span className="text-[#EA001E] ml-0.5">*</span>}
    </label>
  );

  const field = (() => {
    switch (component.type) {
      case 'textarea':
        return (
          <textarea
            className={`${inputClass} min-h-[96px] ${error ? errorInputClass : ''}`}
            placeholder={component.placeholder}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      case 'select':
        return (
          <select
            className={`${inputClass} ${error ? errorInputClass : ''}`}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">{component.placeholder || 'Select…'}</option>
            {(component.options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {(component.options || []).map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 text-[13px] text-[#181818] cursor-pointer">
                <input
                  type="radio"
                  name={component.id}
                  checked={value === opt.value}
                  onChange={() => onChange(opt.value)}
                  className="accent-[var(--form-primary,#0176D3)]"
                />
                {opt.label}
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <label className="flex items-center gap-2 text-[13px] text-[#181818] cursor-pointer">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              className="accent-[var(--form-primary,#0176D3)] w-4 h-4"
            />
            {component.checkboxLabel || 'Yes'}
          </label>
        );
      case 'checkboxGroup': {
        const selected = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {(component.options || []).map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 text-[13px] text-[#181818] cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(opt.value)}
                  onChange={(e) => {
                    onChange(e.target.checked
                      ? [...selected, opt.value]
                      : selected.filter((v) => v !== opt.value));
                  }}
                  className="accent-[var(--form-primary,#0176D3)] w-4 h-4"
                />
                {opt.label}
              </label>
            ))}
          </div>
        );
      }
      case 'file':
        return <FileFieldInput component={component} value={value} error={error} onChange={onChange} onUploadFile={onUploadFile} />;
      case 'signature':
        return <SignatureFieldInput value={value} error={error} onChange={onChange} />;
      case 'rating': {
        const max = component.maxStars || 5;
        const current = Number(value) || 0;
        return (
          <div className="flex items-center gap-1">
            {Array.from({ length: max }, (_, i) => i + 1).map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => onChange(star)}
                className="p-0.5"
                aria-label={`${star} star${star > 1 ? 's' : ''}`}
              >
                <Star
                  size={22}
                  className={star <= current ? 'fill-[var(--form-primary,#0176D3)] text-[var(--form-primary,#0176D3)]' : 'text-[#D8DDE6]'}
                />
              </button>
            ))}
          </div>
        );
      }
      default:
        return (
          <input
            type={meta.inputType || 'text'}
            className={`${inputClass} ${error ? errorInputClass : ''}`}
            placeholder={component.placeholder}
            value={value ?? ''}
            min={meta.inputType === 'number' ? component.min : undefined}
            max={meta.inputType === 'number' ? component.max : undefined}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  })();

  return (
    <div>
      {component.type !== 'checkbox' && label}
      {field}
      {component.helpText && !error && (
        <p className="text-[11px] text-[#706E6B] mt-1">{component.helpText}</p>
      )}
      {error && <p className="text-[11px] text-[#EA001E] mt-1">{error}</p>}
    </div>
  );
}

/**
 * File fields upload immediately on selection (before the form is
 * submitted) rather than sending the raw file with the rest of the
 * values — the public submit endpoint only ever receives small JSON
 * values, and the visitor gets upload/error feedback right away instead
 * of finding out something failed only after hitting Submit. `value` is
 * the uploaded document's metadata ({ documentId, fileName, size }), not
 * a File object. `onUploadFile` is only wired by the real public
 * renderer — the builder canvas passes none, so it's inert there.
 */
function FileFieldInput({ component, value, error, onChange, onUploadFile }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const inputRef = useRef(null);

  const maxBytes = component.maxSizeMB ? component.maxSizeMB * 1024 * 1024 : null;

  const handleFile = async (file) => {
    if (!file) return;
    setUploadError('');
    if (maxBytes && file.size > maxBytes) {
      setUploadError(`File is too large — max ${component.maxSizeMB}MB`);
      return;
    }
    if (!onUploadFile) return;
    setUploading(true);
    try {
      const uploaded = await onUploadFile(file);
      onChange(uploaded);
    } catch (err) {
      setUploadError(err?.response?.data?.message || err.message || 'Upload failed — please try again');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  if (value?.fileName) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 border rounded-[var(--form-radius,4px)] text-[13px] ${error ? errorInputClass : 'border-[#D8DDE6]'}`}>
        <FileText size={15} className="text-[#706E6B] shrink-0" />
        <span className="flex-1 truncate text-[#181818]">{value.fileName}</span>
        {value.size != null && <span className="text-[#706E6B] shrink-0">{formatBytes(value.size)}</span>}
        <button type="button" onClick={() => onChange(null)} className="p-1 text-[#706E6B] hover:text-red-500 shrink-0">
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div>
      <label
        className={`flex items-center gap-2 px-3 py-2 border border-dashed rounded-[var(--form-radius,4px)] text-[13px] cursor-pointer transition-colors ${
          error ? 'border-[#EA001E] text-[#EA001E]' : 'border-[#D8DDE6] text-[#706E6B] hover:border-[var(--form-primary,#0176D3)] hover:text-[var(--form-primary,#0176D3)]'
        }`}
      >
        {uploading ? <Loader2 size={15} className="animate-spin shrink-0" /> : <Paperclip size={15} className="shrink-0" />}
        <span>{uploading ? 'Uploading…' : (component.placeholder || 'Choose a file…')}</span>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={component.accept || undefined}
          disabled={uploading || !onUploadFile}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </label>
      {uploadError && <p className="text-[11px] text-[#EA001E] mt-1">{uploadError}</p>}
    </div>
  );
}

/**
 * Phase 20 — draws directly on a canvas via pointer events (works for
 * mouse and touch alike, no separate desktop/mobile handling needed).
 * `value` is a PNG data URL committed on pointerup, same "small JSON
 * value" contract every other field follows — no separate upload step
 * like FileFieldInput, since a signature is a handful of KB, not a
 * multi-MB attachment.
 */
function SignatureFieldInput({ value, error, onChange }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);

  // The canvas's intrinsic pixel size (500x140) is fixed, but it's
  // stretched to fill the field's actual rendered width via CSS — without
  // rescaling, strokes would land at the wrong spot on any width other
  // than exactly 500px.
  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const start = (e) => {
    if (value) return; // already-signed canvas is read-only until cleared
    drawingRef.current = true;
    lastPointRef.current = getPoint(e);
    canvasRef.current.setPointerCapture(e.pointerId);
  };

  const draw = (e) => {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const point = getPoint(e);
    ctx.strokeStyle = '#181818';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
  };

  const finish = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    onChange(null);
  };

  return (
    <div>
      <div className={`relative border rounded-[var(--form-radius,4px)] ${error ? errorInputClass : 'border-[#D8DDE6]'}`}>
        {value ? (
          <img src={value} alt="Signature" className="w-full h-[140px] object-contain bg-white" />
        ) : (
          <canvas
            ref={canvasRef}
            width={500}
            height={140}
            className="w-full h-[140px] touch-none cursor-crosshair bg-white"
            onPointerDown={start}
            onPointerMove={draw}
            onPointerUp={finish}
            onPointerLeave={finish}
          />
        )}
        {!value && (
          <span className="absolute bottom-1.5 left-2 text-[11px] text-[#D8DDE6] pointer-events-none">Sign here</span>
        )}
      </div>
      <button type="button" onClick={clear} className="mt-1.5 text-[11px] font-bold text-[#706E6B] hover:text-[#EA001E]">
        Clear signature
      </button>
    </div>
  );
}
